import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  lastSavedAt: Date | null;
  onRetry: () => void;
}

// Small autosave status pill. Ported from the vacancy description tab so the
// pre-boarding wizard doesn't depend on recruitment internals.
export function SaveStatusIndicator({ status, lastSavedAt, onRetry }: SaveStatusIndicatorProps) {
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (status !== 'idle' || !lastSavedAt) return;
    const interval = setInterval(() => forceRender((n) => n + 1), 30_000);
    return () => clearInterval(interval);
  }, [status, lastSavedAt]);

  if (status === 'saving') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving…
      </div>
    );
  }

  if (status === 'saved') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-hygge">
        <Check className="h-3.5 w-3.5" />
        Saved just now
      </div>
    );
  }

  if (status === 'error') {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1.5 text-xs text-destructive hover:underline"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Failed to save — Retry
      </button>
    );
  }

  if (lastSavedAt) {
    return (
      <div className="text-xs text-muted-foreground">Saved {formatRelativeTime(lastSavedAt)}</div>
    );
  }
  return null;
}

function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}
